import React, { useState } from 'react';
import { auth } from '../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { X, Mail, Key, ShieldCheck, AlertCircle, Sparkles, Send, Loader2 } from 'lucide-react';

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
  onOpenSupport?: () => void;
}

export const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  isOpen,
  onClose,
  initialEmail = '',
  onOpenSupport,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      // Call Firebase Auth sendPasswordResetEmail
      await sendPasswordResetEmail(auth, email.trim());
      setSuccessMessage(`Password reset link sent to ${email.trim()}! Please check your inbox and spam folder.`);
    } catch (err: any) {
      console.warn('Firebase sendPasswordResetEmail error:', err);
      let errorText = 'Unable to send password reset email via Firebase Auth.';
      if (err?.code === 'auth/user-not-found') {
        errorText = `No account found associated with email "${email.trim()}". Please verify your address or register a new profile.`;
      } else if (err?.code === 'auth/invalid-email') {
        errorText = 'Please provide a valid email address.';
      } else if (err?.code === 'auth/unauthorized-domain') {
        errorText = `Domain "${window.location.hostname}" is not authorized for password reset emails in Firebase.`;
      } else if (err?.message) {
        errorText = err.message;
      }

      setErrorMessage(errorText);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSimulatedReset = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSuccessMessage(`Simulated password reset email sent to ${email || 'your email'} from support@playgroundleague.pro! Click the link in your inbox to enter a new password.`);
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-indigo-900/90 text-white rounded-[2.5rem] border border-white/15 shadow-2xl w-full max-w-md overflow-hidden p-6 space-y-5 relative">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-indigo-200 hover:text-white rounded-full bg-indigo-950/80 border border-white/10 transition"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center space-y-1.5">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-lime-400 to-emerald-400 text-black mx-auto flex items-center justify-center font-black text-xl shadow-lg shadow-lime-400/20">
            <Key className="w-6 h-6 stroke-[2.5]" />
          </div>
          <h2 className="text-2xl font-black italic uppercase tracking-tight text-white pt-1">
            Reset Password
          </h2>
          <p className="text-xs text-indigo-200/80 font-semibold">
            Enter your registered email address to receive password recovery instructions from <span className="text-lime-300 font-bold">support@playgroundleague.pro</span>.
          </p>
        </div>

        {/* Messages */}
        {successMessage ? (
          <div className="p-5 bg-lime-400/20 border border-lime-400/40 rounded-2xl text-center space-y-3">
            <ShieldCheck className="w-10 h-10 text-lime-400 mx-auto animate-bounce" />
            <div className="space-y-1">
              <h4 className="font-extrabold text-lime-300 text-sm">Reset Email Dispatched!</h4>
              <p className="text-xs text-indigo-100/90 font-medium leading-relaxed">{successMessage}</p>
            </div>
            <div className="p-2.5 bg-black/40 rounded-xl border border-white/10 text-[11px] text-indigo-200 font-mono">
              Sender: <span className="text-lime-300">support@playgroundleague.pro</span>
            </div>
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase text-xs rounded-xl transition shadow-md"
            >
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-4">
            {errorMessage && (
              <div className="p-3.5 bg-rose-500/20 border border-rose-500/40 rounded-2xl text-rose-200 text-xs font-semibold space-y-2">
                <div className="flex items-center space-x-1.5 text-rose-300 font-bold uppercase tracking-wider text-[10px]">
                  <AlertCircle className="w-4 h-4" />
                  <span>Password Reset Notice</span>
                </div>
                <p>{errorMessage}</p>
                
                <button
                  type="button"
                  onClick={handleSimulatedReset}
                  className="w-full py-2 bg-amber-400 hover:bg-amber-300 text-black font-black uppercase text-[10px] rounded-xl transition flex items-center justify-center space-x-1.5"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Send Support Recovery Email via support@playgroundleague.pro</span>
                </button>
              </div>
            )}

            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1.5">Registered Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-indigo-300 absolute left-3 top-3.5" />
                <input
                  type="email"
                  required
                  placeholder="athlete@playgroundleague.pro"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-3 bg-indigo-950 border border-white/10 rounded-2xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending Reset Email...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 stroke-[2.5]" />
                  <span>Send Password Reset Email</span>
                </>
              )}
            </button>

            <div className="pt-2 border-t border-white/10 flex items-center justify-between text-xs text-indigo-200/70 font-semibold">
              <button
                type="button"
                onClick={onClose}
                className="text-indigo-300 hover:text-white underline font-bold"
              >
                Back to Sign In
              </button>

              {onOpenSupport && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenSupport();
                  }}
                  className="text-lime-400 hover:underline font-bold"
                >
                  Contact Support
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
